import React from 'react';
import './home-section.scss';
import { ChannelCardV2 } from './channel-card/channel-card';
import ContentCardPlaceholder from '../placeholders/content-card-placeholder';
import { FeedCardWithData } from '../content/solar-feed-card/solar-feed-card';
import { useContentFeedQuery } from '../../common/queries/use-content-feed-query';
import {
  FeedCardGrid,
  FeedCardGridItem,
} from '@socialchorus/shared-ui-components';
import useScreenSize from '../../common/use-screen-size';
import useFeatureFlag from '../../common/use-feature-flag';
import { Feature } from '../../models/features/features';
import FeedHeader, { FeedViewAllFooter } from './content-layouts/feed-header';
import { MobileHorizontalFeedCards } from './content-layouts/horizontal-scroll-row';

/**
 * Interface for HomeSection props
 */
type IHomeSection = {
  title: string;
  channelId: string;
  minLength: number;
  maxLength: number;
  analyticsData: IAnalyticsData;
};

/**
 * HomeSection component displays a section with content cards
 */
export const HomeSection: React.FC<IHomeSection> = ({
  title,
  channelId,
  minLength = 1,
  maxLength = 4,
  analyticsData,
}) => {
  const { isMobile } = useScreenSize();
  const mobileWebParity = useFeatureFlag(
    Feature.CUSTOM_HOMEPAGE_MOBILE_WEB_PARITY
  );
  const isMobileHorizontal = isMobile && !!mobileWebParity;
  const { data, isLoading } = useContentFeedQuery({
    channelId: channelId,
    perPage: 4,
  });
  if (!isLoading && data && data.length < minLength) {
    return null;
  }
  const contentToShow = data?.slice(0, maxLength);

  const sectionId = `channel-${channelId}`;
  const feedUrl = `/channels/${channelId}`;

  return (
    <section className="home-section" aria-labelledby={`${sectionId}--title`}>
      {isMobileHorizontal ? (
        <FeedHeader
          id={sectionId}
          title={title}
          viewAllLink={feedUrl}
          viewAllContext={title}
          isTopic={true}
          analyticsData={analyticsData}
          isMobileHorizontal
        />
      ) : (
        <div className="section-header">
          <h2 id={`${sectionId}--title`} className="title">
            {title}
          </h2>
        </div>
      )}
      <ChannelCardV2 channelId={channelId} analyticsData={analyticsData} />
      {isMobileHorizontal ? (
        <>
          {isLoading ? (
            <ContentCardPlaceholder />
          ) : (
            contentToShow && (
              <>
                <MobileHorizontalFeedCards
                  contents={contentToShow}
                  analyticsData={analyticsData}
                  title={title}
                />
                <FeedViewAllFooter
                  viewAllLink={feedUrl}
                  viewAllContext={title}
                  isTopic={true}
                  analyticsData={analyticsData}
                />
              </>
            )
          )}
        </>
      ) : (
        <FeedCardGrid columns={isMobile ? 1 : 2}>
          {isLoading
            ? [...Array(maxLength)].map((_, idx) => (
                <FeedCardGridItem key={idx}>
                  <ContentCardPlaceholder />
                </FeedCardGridItem>
              ))
            : contentToShow?.map((content) => (
                <FeedCardGridItem key={content.id}>
                  <FeedCardWithData
                    content={content}
                    analyticsData={analyticsData}
                    layout="horizontal"
                    fullHeight
                  />
                </FeedCardGridItem>
              ))}
        </FeedCardGrid>
      )}
    </section>
  );
};
